import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import store, { borrowerActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { PrincipalSelector, EntitySelector } from '../common/BorrowerComponents';
import { NumericFormat } from 'react-number-format';
import { LoadPanel } from 'devextreme-react/load-panel';
import { RefreshEvents } from '../../common/SharedEvents';
import { LinkType } from '../common/BorrowerConstants';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';

const editBorrowerEntityToPrincipalLinkModal = () => {
    const {
        isOpen,
        entityId,
        principalId,
        linkType = LinkType.Principal,
    } = useSelector((state) => state.borrower.editBorrowerEntityToPrincipalLinkModal);
    const { dispatch } = store;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [eId, set_eId] = useState(null);
    const [entityName, set_entityName] = useState('');
    const [pid, set_pid] = useState(null);
    const [principalName, set_principalName] = useState('');
    const [percentOwnership, set_percentOwnership] = useState(null);
    const [isOfficer, set_isOfficer] = useState(false);
    const [title, set_title] = useState(null);

    //Dialogs
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const handleClose = () =>
        dispatch(
            borrowerActions.setEditBorrowerEntityToPrincipalLinkModal({
                editBorrowerEntityToPrincipalLinkModal: { isOpen: false },
            })
        );

    const styles = {
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        sectionHeader: { margin: '10px 0' },
        formColumn: { paddingBottom: '5px', paddingRight: '10px' },
        isofficer: { paddingTop: '30px' },
    };

    const resetState = () => {
        set_eId(null);
        set_entityName('');
        set_pid(null);
        set_principalName('');
        set_percentOwnership(null);
        set_isOfficer(false);
        set_title(null);
    };

    useEffect(() => {
        resetState();
    }, [isOpen]);

    useEffect(() => {
        if (!entityId && !principalId) {
            return;
        }

        switch (linkType) {
            case LinkType.Principal:
                set_eId(entityId);
                break;
            case LinkType.Entity:
                set_pid(principalId);
                break;
        }

        const loadEntityContactLink = async () => {
            const response = await borrowerApi.getEntityPrincipalLink(entityId, principalId);
            if (response.statusCode !== 200) {
                return;
            }

            const link = response.result;
            set_eId(link.entityId);
            set_entityName(link.entityName);
            set_pid(link.principalId);
            set_principalName(link.principalName);
            set_percentOwnership(link.percentOwnership);
            set_isOfficer(link.isOfficer);
            set_title(link.title);
        };
        loadEntityContactLink();
    }, [entityId, principalId]);

    const onSelectedPrincipal = (principal) => {
        set_pid(principal?.principalId);
        set_principalName(principal?.name);
    };

    const onSelectedEntity = (entity) => {
        set_eId(entity?.entityId);
        set_entityName(entity?.name);
    };

    const saveLink = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            set_loadingMessage('Saving changes...');
            set_loadingOpen(true);

            let model = {
                entityId: eId,
                principalId: pid,
                isOfficer,
                title,
                percentOwnership,
            };
            const response = await borrowerApi.setEntityPrincipalLink(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();
            set_loadingOpen(false);

            handleAdminSuccess('Entity-Contact Link saved successfully.');

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            maxWidth='lg'
            open={isOpen}
            onClose={handleClose}
            aria-labelledby='form-dialog-title'
        >
            <div className='modal-container'>
                <div className='modal-backdrop'></div>
                <div
                    className='modal'
                    tabIndex='-1'
                    role='dialog'
                    style={{ width: '800px', overflow: 'unset' }}
                >
                    <div
                        className='modal-dialog'
                        role='document'
                    >
                        <div
                            className='page-header'
                            role='banner'
                        >
                            <h1
                                style={{
                                    height: '50px',
                                    marginTop: '15px',
                                    flex: '0 0 80%',
                                    maxWidth: '80%',
                                }}
                            >
                                {entityId && principalId ? 'Edit Entity-Principal Link' : 'Add Entity-Principal Link'}{' '}
                            </h1>
                            <button
                                type='button'
                                className='close'
                                data-dismiss='modal'
                                aria-label='Close'
                                onClick={handleClose}
                            >
                                <span aria-hidden='true'>&times;</span>
                            </button>
                        </div>
                    </div>
                    <div
                        className='main-content no-search'
                        style={{ padding: '0', margin: '0' }}
                    >
                        <form
                            name='editBorrowerForm'
                            onSubmit={(event) => saveLink(event)}
                            className={'standard-form'}
                        >
                            <div style={{ padding: '10px', minHeight: '300px' }}>
                                <div className='row'>
                                    <div
                                        className='columns small-12 large-12'
                                        style={styles.formColumn}
                                    >
                                        {linkType === LinkType.Principal && entityName && (
                                            <div>
                                                <h2>{entityName}</h2>
                                                linked to
                                            </div>
                                        )}
                                        {linkType === LinkType.Entity && principalName && (
                                            <div>
                                                <h2>{principalName}</h2>
                                                linked to
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className='row'>
                                    <div
                                        className='columns small-12 large-12'
                                        style={styles.formColumn}
                                    >
                                        {linkType === LinkType.Principal && (
                                            <PrincipalSelector
                                                principalId={principalId}
                                                excludedEntityId={entityId}
                                                onSelect={onSelectedPrincipal}
                                                disabled={principalId}
                                                title='Search Principals'
                                            />
                                        )}
                                        {linkType === LinkType.Entity && (
                                            <EntitySelector
                                                entityId={entityId}
                                                excludedPrincipalId={principalId}
                                                onSelect={onSelectedEntity}
                                                disabled={entityId}
                                                title='Search Entities'
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className='row'>
                                    <div
                                        className='columns small-12 large-2'
                                        style={styles.formColumn}
                                    >
                                        <label htmlFor='percentOwnership'>Ownership %</label>
                                        <NumericFormat
                                            thousandSeparator=','
                                            suffix='%'
                                            id='percentOwnership'
                                            value={percentOwnership ?? ''}
                                            decimalScale={2}
                                            allowNegative={false}
                                            onValueChange={(values) => {
                                                set_percentOwnership(values.floatValue);
                                            }}
                                            required={false}
                                            isAllowed={(values, sourceInfo) => {
                                                const value = values?.floatValue;
                                                return value === undefined || value === null || (value >= 0 && value <= 100);
                                            }}
                                            disabled={!pid || !eId}
                                        />
                                    </div>
                                    <div
                                        className='columns small-12 large-6'
                                        style={styles.formColumn}
                                    >
                                        <label htmlFor='title'>Title</label>
                                        <input
                                            id='title'
                                            type='text'
                                            value={title}
                                            onChange={(e) => set_title(e.target.value)}
                                            maxLength={50}
                                            disabled={!pid || !eId}
                                        />
                                    </div>
                                    <div
                                        className='columns small-12 large-3'
                                        style={styles.isofficer}
                                    >
                                        <input
                                            id='isOfficer'
                                            type='checkbox'
                                            checked={isOfficer}
                                            onChange={(e) => set_isOfficer(e.target.checked)}
                                            disabled={!pid || !eId}
                                        />{' '}
                                        Is Officer?
                                    </div>
                                </div>
                            </div>

                            <div
                                className='button-footer'
                                style={styles.buttonContainer}
                            >
                                <button
                                    style={styles.buttonMarginRight}
                                    type='submit'
                                    className='button primary'
                                    title='Save changes and close this form'
                                    disabled={!pid || !eId}
                                >
                                    Save
                                </button>
                                &nbsp;
                                <button
                                    type='button'
                                    className='button hollow'
                                    data-dismiss='modal'
                                    onClick={handleClose}
                                    title='Abandon changes and close'
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </Dialog>
    );
};

export default editBorrowerEntityToPrincipalLinkModal;
